// Browser-based image compression using Canvas API
// This serves as a fallback when TinyPNG API is not available due to CORS or other issues

/**
 * Compresses an image using browser Canvas API
 * @param {File} file - The image file to compress
 * @param {Object} options - Compression options
 * @param {number} options.quality - JPEG quality (0.1 to 1.0)
 * @param {number} options.maxWidth - Maximum width in pixels
 * @param {number} options.maxHeight - Maximum height in pixels
 * @param {Function} onProgress - Progress callback
 * @returns {Promise<File>} - Compressed image file
 */
export const compressImageWithCanvas = async (file, options = {}, onProgress = null) => {
  const {
    quality = 0.8,
    maxWidth = 1920,
    maxHeight = 1080,
    maintainAspectRatio = true,
  } = options;

  if (onProgress) onProgress(10);

  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      try {
        if (onProgress) onProgress(30);

        // Calculate new dimensions
        let { width, height } = img;
        
        if (maintainAspectRatio) {
          const aspectRatio = width / height;
          
          if (width > maxWidth) {
            width = maxWidth;
            height = width / aspectRatio;
          }
          
          if (height > maxHeight) {
            height = maxHeight;
            width = height * aspectRatio;
          }
        } else {
          width = Math.min(width, maxWidth);
          height = Math.min(height, maxHeight);
        }

        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;

        if (onProgress) onProgress(50);

        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);

        if (onProgress) onProgress(70);

        // Convert to blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Canvas compression failed'));
              return;
            }

            if (onProgress) onProgress(90);

            // Create new file with compression metadata
            const compressedFile = new File([blob], file.name, {
              type: file.type === 'image/png' ? 'image/png' : 'image/jpeg',
              lastModified: Date.now(),
            });

            // Add compression metadata
            const originalSize = file.size;
            const compressedSize = compressedFile.size;
            const savedBytes = Math.max(0, originalSize - compressedSize);
            const compressionRatio = originalSize > 0 ? Math.round((savedBytes / originalSize) * 100) : 0;

            compressedFile.compressed = true;
            compressedFile.compressionStats = {
              originalSize,
              compressedSize,
              savedBytes,
              compressionRatio,
              method: 'browser-canvas',
            };

            if (onProgress) onProgress(100);
            resolve(compressedFile);
          },
          file.type === 'image/png' ? 'image/png' : 'image/jpeg',
          file.type === 'image/png' ? 1 : quality
        );
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image for compression'));
    };

    if (onProgress) onProgress(5);

    // Load image
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Checks if browser-based compression is supported
 */
export const isBrowserCompressionSupported = () => {
  try {
    const canvas = document.createElement('canvas');
    return !!(canvas.getContext && canvas.getContext('2d'));
  } catch (error) {
    return false;
  }
};

/**
 * Gets optimal compression settings based on file size and type
 */
export const getOptimalCompressionSettings = (file) => {
  const settings = {
    quality: 0.8,
    maxWidth: 1920,
    maxHeight: 1080,
  };

  // Adjust quality based on file size
  if (file.size > 5 * 1024 * 1024) { // > 5MB
    settings.quality = 0.7;
    settings.maxWidth = 1600;
    settings.maxHeight = 900;
  } else if (file.size > 2 * 1024 * 1024) { // > 2MB
    settings.quality = 0.75;
    settings.maxWidth = 1800;
    settings.maxHeight = 1000;
  } else if (file.size < 100 * 1024) { // < 100KB
    // Don't compress small files much
    settings.quality = 0.9;
  }

  // PNG files need different handling
  if (file.type === 'image/png') {
    settings.quality = 1; // PNG doesn't use quality, but we can resize
    // For PNG, focus on resizing rather than quality reduction
  }

  return settings;
};

/**
 * Estimates compression savings for preview purposes
 */
export const estimateBrowserCompressionSavings = (file) => {
  if (!file.type.startsWith('image/')) return null;

  const settings = getOptimalCompressionSettings(file);
  
  // Rough estimation based on file type and settings
  let estimatedRatio = 0;
  
  if (file.type === 'image/jpeg') {
    estimatedRatio = Math.max(0.3, 1 - settings.quality);
  } else if (file.type === 'image/png') {
    estimatedRatio = 0.2; // PNG compression is more limited
  } else if (file.type === 'image/webp') {
    estimatedRatio = 0.4;
  }

  // Factor in resizing
  const maxPixels = settings.maxWidth * settings.maxHeight;
  // Estimate original pixels (this is rough)
  const estimatedOriginalPixels = Math.max(maxPixels, file.size / 3); // Rough estimate
  const resizeRatio = Math.min(1, maxPixels / estimatedOriginalPixels);
  
  // Combine quality and resize compression
  const totalRatio = 1 - ((1 - estimatedRatio) * resizeRatio);
  
  return {
    estimatedSavings: Math.round(file.size * totalRatio),
    estimatedFinalSize: Math.round(file.size * (1 - totalRatio)),
    estimatedRatio: Math.round(totalRatio * 100),
  };
}; 